/* eslint-disable sort-imports */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    escapeHtml,
    isBodyEmpty,
    sanitizeLinkUrl,
    sanitizeNewsHtml,
} from 'src/utils/critical-organization-updates';
import {
    BlockquoteIcon,
    BoldIcon,
    CleanIcon,
    ItalicIcon,
    LinkIcon,
    ListBulletIcon,
    ListOrderedIcon,
    RedoIcon,
    StrikeIcon,
    UnderlineIcon,
    UndoIcon,
    UnlinkIcon,
} from './icons';

import './styles.scss';

type BlockTag = 'p' | 'h1' | 'h2' | 'h3';

interface IToolbarButton {
    id: string;
    label: string;
    Icon: React.FC;
    command: string;
    // Shown pressed when document.queryCommandState reports it for the selection.
    isStateful?: boolean;
}

const BLOCK_TAGS = ['p', 'h1', 'h2', 'h3', 'blockquote', 'li'];
const NO_RANGES = 0;
const FIRST_RANGE = 0;
const BLOCK_OPTIONS: Array<{ value: BlockTag; label: string }> = [
    { value: 'p', label: 'Normal (paragraph)' },
    { value: 'h1', label: 'Heading 1' },
    { value: 'h2', label: 'Heading 2' },
    { value: 'h3', label: 'Heading 3' },
];

const FORMAT_BUTTONS: IToolbarButton[] = [
    {
        id: 'bold',
        label: 'Bold',
        Icon: BoldIcon,
        command: 'bold',
        isStateful: true,
    },
    {
        id: 'italic',
        label: 'Italic',
        Icon: ItalicIcon,
        command: 'italic',
        isStateful: true,
    },
    {
        id: 'underline',
        label: 'Underline',
        Icon: UnderlineIcon,
        command: 'underline',
        isStateful: true,
    },
    {
        id: 'strike',
        label: 'Strikethrough',
        Icon: StrikeIcon,
        command: 'strikeThrough',
        isStateful: true,
    },
];

const LIST_BUTTONS: IToolbarButton[] = [
    {
        id: 'bulletList',
        label: 'Bulleted list',
        Icon: ListBulletIcon,
        command: 'insertUnorderedList',
        isStateful: true,
    },
    {
        id: 'numberList',
        label: 'Numbered list',
        Icon: ListOrderedIcon,
        command: 'insertOrderedList',
        isStateful: true,
    },
];

const getEditorSelection = (editor: HTMLElement | null): Selection | null => {
    const selection = window.getSelection();

    return editor &&
        selection &&
        selection.rangeCount > NO_RANGES &&
        selection.anchorNode &&
        editor.contains(selection.anchorNode)
        ? selection
        : null;
};

const getCurrentBlock = (editor: HTMLElement, selection: Selection) => {
    let node: Node | null = selection.anchorNode;

    while (node && node !== editor) {
        if (node instanceof Element) {
            const tag = node.tagName.toLowerCase();
            if (BLOCK_TAGS.includes(tag)) return tag;
        }
        node = node.parentNode;
    }

    return 'p';
};

const queryState = (command: string): boolean => {
    try {
        return document.queryCommandState(command);
    } catch {
        return false;
    }
};

interface IRichTextEditorProps {
    id: string;
    labelledBy: string;
    placeholder: string;
    value: string;
    onChange: (html: string) => void;
    hasError?: boolean;
}

const RichTextEditor: React.FC<IRichTextEditorProps> = ({
    id,
    labelledBy,
    placeholder,
    value,
    onChange,
    hasError = false,
}) => {
    const { t } = useTranslation();
    const editorRef = useRef<HTMLDivElement>(null);
    const lastEmitted = useRef('');
    const savedRange = useRef<Range | null>(null);
    const [activeBlock, setActiveBlock] = useState('p');
    const [activeCommands, setActiveCommands] = useState<
        Record<string, boolean>
    >({});
    const [linkUrl, setLinkUrl] = useState('');
    const [linkError, setLinkError] = useState('');
    const [isLinkPanelOpen, setIsLinkPanelOpen] = useState(false);

    const text = (key: string, defaultValue: string) =>
        t(`SystemAdmin.CriticalOrganizationUpdates.Editor.${key}`, {
            defaultValue,
        });

    useEffect(() => {
        if (editorRef.current && value !== lastEmitted.current) {
            editorRef.current.innerHTML = value;
            lastEmitted.current = value;
        }
    }, [value]);

    useEffect(() => {
        try {
            document.execCommand('defaultParagraphSeparator', false, 'p');
        } catch {
            // Not supported (e.g. jsdom); the sanitizer also maps div to p.
        }
    }, []);

    const emit = useCallback(() => {
        if (!editorRef.current) return;

        const editorHtml = editorRef.current.innerHTML;
        editorRef.current.dataset.empty = String(isBodyEmpty(editorHtml));
        const html = sanitizeNewsHtml(editorHtml);
        lastEmitted.current = html;
        onChange(html);
    }, [onChange]);

    const refreshToolbarState = useCallback(() => {
        const selection = getEditorSelection(editorRef.current);
        if (!editorRef.current || !selection) return;

        savedRange.current = selection.getRangeAt(FIRST_RANGE).cloneRange();
        setActiveBlock(getCurrentBlock(editorRef.current, selection));
        setActiveCommands({
            bold: queryState('bold'),
            italic: queryState('italic'),
            underline: queryState('underline'),
            strikeThrough: queryState('strikeThrough'),
            insertUnorderedList: queryState('insertUnorderedList'),
            insertOrderedList: queryState('insertOrderedList'),
        });
    }, []);

    useEffect(() => {
        document.addEventListener('selectionchange', refreshToolbarState);

        return () =>
            document.removeEventListener(
                'selectionchange',
                refreshToolbarState,
            );
    }, [refreshToolbarState]);

    const restoreSelection = () => {
        const editor = editorRef.current;
        if (!editor) return;

        const range = savedRange.current;
        editor.focus();
        // Focusing can create a new caret inside the editor; restore the saved range.
        if (range) {
            const selection = window.getSelection();
            selection?.removeAllRanges();
            selection?.addRange(range);
        }
    };

    const run = (command: string, argument?: string) => {
        restoreSelection();
        document.execCommand(command, false, argument);
        emit();
        refreshToolbarState();
    };

    const handleBlockChange = (tag: string) => {
        run('formatBlock', `<${tag}>`);
    };

    const handleBlockquote = () => {
        run(
            'formatBlock',
            activeBlock === 'blockquote' ? '<p>' : '<blockquote>',
        );
    };

    const handleClearFormatting = () => {
        restoreSelection();
        document.execCommand('removeFormat');
        document.execCommand('unlink');
        document.execCommand('formatBlock', false, '<p>');
        emit();
        refreshToolbarState();
    };

    const openLinkPanel = () => {
        refreshToolbarState();
        setLinkUrl('');
        setLinkError('');
        setIsLinkPanelOpen(true);
    };

    const closeLinkPanel = () => {
        setIsLinkPanelOpen(false);
        setLinkError('');
    };

    const applyLink = () => {
        const href = sanitizeLinkUrl(linkUrl);
        if (!href) {
            setLinkError(
                text(
                    'LinkError',
                    'Enter a valid link starting with http://, https:// or mailto:',
                ),
            );
            return;
        }

        restoreSelection();
        const selection = window.getSelection();

        if (selection?.isCollapsed) {
            document.execCommand(
                'insertHTML',
                false,
                `<a href="${escapeHtml(href)}">${escapeHtml(href)}</a>`,
            );
        } else {
            document.execCommand('createLink', false, href);
        }
        emit();
        closeLinkPanel();
    };

    const handlePaste = (event: React.ClipboardEvent<HTMLDivElement>) => {
        event.preventDefault();

        // Formatting and links are kept, but only what the sanitizer allows.
        const cleanHtml = sanitizeNewsHtml(
            event.clipboardData.getData('text/html'),
        );

        if (cleanHtml && !isBodyEmpty(cleanHtml)) {
            document.execCommand('insertHTML', false, cleanHtml);
            return;
        }

        document.execCommand(
            'insertText',
            false,
            event.clipboardData.getData('text/plain'),
        );
    };

    const renderIconButton = (
        id: string,
        label: string,
        Icon: React.FC,
        onClick: () => void,
        isActive?: boolean,
    ) => (
        <button
            key={id}
            type='button'
            className={`rich-text-editor__btn ${isActive ? 'is-active' : ''}`}
            aria-label={text(id, label)}
            title={text(id, label)}
            aria-pressed={isActive}
            onClick={onClick}
        >
            <Icon />
        </button>
    );

    const renderButton = (button: IToolbarButton) =>
        renderIconButton(
            button.id,
            button.label,
            button.Icon,
            () => run(button.command),
            button.isStateful
                ? Boolean(activeCommands[button.command])
                : undefined,
        );

    return (
        <div
            className={`rich-text-editor ${hasError ? 'is-error' : ''}`}
            data-testid={`${id}-wrapper`}
        >
            <div
                className='rich-text-editor__toolbar'
                role='toolbar'
                aria-label={text('Toolbar', 'Text formatting')}
                onMouseDown={(event) => {
                    // Keep the editor selection when a toolbar button is clicked.
                    if (!(event.target instanceof HTMLSelectElement)) {
                        event.preventDefault();
                    }
                }}
            >
                <span className='rich-text-editor__formats'>
                    <select
                        className='rich-text-editor__select'
                        aria-label={text('BlockStyle', 'Text style')}
                        value={
                            BLOCK_OPTIONS.some(
                                (option) => option.value === activeBlock,
                            )
                                ? activeBlock
                                : 'p'
                        }
                        onChange={(event) =>
                            handleBlockChange(event.target.value)
                        }
                    >
                        {BLOCK_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                                {text(`Block.${option.value}`, option.label)}
                            </option>
                        ))}
                    </select>
                </span>
                <span className='rich-text-editor__formats'>
                    {FORMAT_BUTTONS.map(renderButton)}
                </span>
                <span className='rich-text-editor__formats'>
                    {LIST_BUTTONS.map(renderButton)}
                </span>
                <span className='rich-text-editor__formats'>
                    {renderIconButton(
                        'blockquote',
                        'Blockquote',
                        BlockquoteIcon,
                        handleBlockquote,
                        activeBlock === 'blockquote',
                    )}
                    {renderIconButton(
                        'link',
                        'Insert link',
                        LinkIcon,
                        openLinkPanel,
                    )}
                    {renderIconButton('unlink', 'Remove link', UnlinkIcon, () =>
                        run('unlink'),
                    )}
                </span>
                <span className='rich-text-editor__formats'>
                    {renderIconButton('undo', 'Undo', UndoIcon, () =>
                        run('undo'),
                    )}
                    {renderIconButton('redo', 'Redo', RedoIcon, () =>
                        run('redo'),
                    )}
                </span>
                <span className='rich-text-editor__formats'>
                    {renderIconButton(
                        'clear',
                        'Clear formatting',
                        CleanIcon,
                        handleClearFormatting,
                    )}
                </span>
            </div>

            {isLinkPanelOpen && (
                <div className='rich-text-editor__link-panel'>
                    <div className='c-input'>
                        <input
                            className={`c-input__input ${
                                linkError ? 'is-error' : ''
                            }`}
                            type='text'
                            autoFocus
                            aria-label={text('LinkUrl', 'Link URL')}
                            placeholder='https://'
                            value={linkUrl}
                            onChange={(event) => {
                                setLinkUrl(event.target.value);
                                setLinkError('');
                            }}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter') {
                                    event.preventDefault();
                                    applyLink();
                                } else if (event.key === 'Escape') {
                                    closeLinkPanel();
                                }
                            }}
                        />
                    </div>
                    <button
                        type='button'
                        className='c-btn c-btn--secondary'
                        onClick={applyLink}
                    >
                        <span className='c-btn__text'>
                            {text('LinkApply', 'Apply')}
                        </span>
                    </button>
                    <button
                        type='button'
                        className='c-btn c-btn--secondary'
                        onClick={closeLinkPanel}
                    >
                        <span className='c-btn__text'>
                            {text('LinkCancel', 'Cancel')}
                        </span>
                    </button>
                    {linkError && (
                        <span className='c-error-message'>{linkError}</span>
                    )}
                </div>
            )}

            <div
                id={id}
                ref={editorRef}
                className='rich-text-editor__content'
                role='textbox'
                aria-multiline='true'
                aria-labelledby={labelledBy}
                aria-placeholder={placeholder}
                data-placeholder={placeholder}
                data-empty={isBodyEmpty(value)}
                aria-invalid={hasError}
                contentEditable
                suppressContentEditableWarning
                onInput={emit}
                onBlur={emit}
                onPaste={handlePaste}
                onDrop={(event) => event.preventDefault()}
            />
        </div>
    );
};

export default RichTextEditor;
